import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import tw from "twin.macro";
import styled from "styled-components";
import cogoToast from "cogo-toast";

import logo from "images/images/smiles_and_laughters_logo2.png";
import illustration from "images/images/focused-young-black-woman-holding-basket-with-lettuce-on-4177750 (1).jpg";
import { ReactComponent as SignUpIcon } from "feather-icons/dist/icons/pocket.svg";

import { addUser, addPartner, fetchUsers } from "DBServices/firestoreDBService";
import { APP_NAME } from "../constants";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container as ContainerBase } from "components/misc/Layouts";
import Navbar from "components/Navbar";
import ChangePassword from "components/ChangePassword";

const Container = styled(ContainerBase)`
    ${tw`min-h-screen text-white font-medium flex justify-center mt-0`}
  background: linear-gradient(135deg, #022c22 0%, #115e59 100%);
`;
const Content = tw.div`max-w-screen-xl m-0 sm:mx-20 sm:my-16 bg-white text-gray-900 shadow sm:rounded-2xl flex justify-center flex-1 overflow-hidden`;
const MainContainer = tw.div`lg:w-1/2 xl:w-5/12 p-6 sm:p-12 flex flex-col justify-center`;
const LogoImage = tw.img`h-12 mx-auto`;
const MainContent = tw.div`mt-4 flex flex-col items-center`;
const Heading = tw.h1`text-2xl xl:text-3xl font-extrabold text-gray-800 text-center`;
const Subheading = tw.p`text-sm text-gray-500 mt-2 text-center`;
const IllustrationContainer = tw.div`sm:rounded-r-lg flex-1 bg-gray-100 text-center hidden lg:flex justify-center items-center`;
const IllustrationImage = styled.div`
  ${props => `background-image: url("${props.imageSrc}");`}
  ${tw`m-12 xl:m-16 w-full max-w-lg h-80 bg-cover bg-center bg-no-repeat rounded-2xl shadow-md`}
`;

const Form = tw.form`w-full mt-6`;
const Input = tw.input`w-full px-4 py-3 rounded-xl font-medium bg-gray-100 border border-gray-200 placeholder-gray-400 text-sm focus:outline-none focus:border-green-600 focus:bg-white transition duration-200 mt-3 first:mt-0`;
const Textarea = tw.textarea`w-full px-4 py-3 rounded-xl font-medium bg-gray-100 border border-gray-200 placeholder-gray-400 text-sm focus:outline-none focus:border-green-600 focus:bg-white transition duration-200 mt-3 h-20`;
const SubmitButton = styled.button`
  ${tw`mt-5 tracking-wide font-bold text-white w-full py-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none transform hover:-translate-y-1`}
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  .icon { ${tw`w-5 h-5 -ml-2`} }
  .text { ${tw`ml-3`} }
`;
const ErrorMsg = tw.div`mt-3 text-red-500 text-xs`;

// Account type tabs
const TypeTabRow = tw.div`flex w-full mt-6 mb-2 rounded-xl overflow-hidden border border-gray-200`;
const TypeTab = styled.button`
  ${tw`flex-1 py-3 text-sm font-bold transition`}
  ${props => props.active ? `background-color: #059669; color: white;` : tw`bg-gray-100 text-gray-600 hover:bg-gray-200`}
`;

// Partner sub-type tabs
const SubTabRow = tw.div`flex flex-wrap mt-4 mb-2`;
const SubTab = styled.button`
  ${tw`px-3 py-2 text-xs font-bold rounded-full border transition`}
  ${props => props.active
    ? `background-color: #059669; color: white; border-color: #059669; box-shadow: 0 1px 2px rgba(0,0,0,0.1);`
    : tw`bg-white text-gray-600 border-gray-200 hover:border-green-500 hover:text-green-600`}
`;

const SectionLabel = tw.p`text-xs font-bold text-gray-500 uppercase tracking-wider mt-4 mb-1`;
const InfoBox = tw.div`mt-3 p-3 bg-blue-100 border border-blue-200 rounded-lg text-xs text-blue-700`;

const PARTNER_TYPES = [
    { key: "market", label: "🏪 Market", desc: `Sell food items through your own market storefront on ${APP_NAME}.` },
    { key: "farm", label: "🌾 Farm", desc: "Supply fresh produce directly from your farm to customers." },
    { key: "wholesale", label: "📦 Wholesale Store", desc: "Offer bulk items at wholesale prices." },
    { key: "dispatcher", label: "🚚 Dispatcher/Driver", desc: "Handle last-mile delivery for orders." },
    { key: "logistics", label: "🏭 Logistics Partner", desc: "Provide warehousing and transport services." },
];

const SERVICE_AREAS = ["Ikeja", "Lekki", "Yaba", "Gbagada", "Surulere", "Victoria Island", "Ikorodu", "Ajah", "Festac", "Apapa"];

export default function CompleteProfilePage() {
    const history = useHistory();
    const auth = getAuth();
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const [accountType, setAccountType] = useState("customer"); // "customer" | "partner"
    const [partnerType, setPartnerType] = useState("market");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState("");

    // Shared fields
    const [form, setForm] = useState({
        firstname: "", lastname: "", username: "", email: "", phone: "",
    });

    // Partner-specific fields
    const [partnerForm, setPartnerForm] = useState({
        businessName: "", businessAddress: "", businessPhone: "", businessEmail: "",
        serviceAreas: [], description: "", vehicleType: "", licenseNumber: "",
        warehouseCapacity: "", certifications: "",
    });

    useEffect(() => {
        const checkAuth = async () => {
            const user = auth.currentUser;
            if (!user) {
                cogoToast.error("Authentication required to access this page.");
                history.push("/login");
                return;
            }

            // Fetch users to verify if profile already exists
            try {
                const allUsers = await fetchUsers();
                const existingUser = allUsers.find(u => u.uid === user.uid || u.Email === user.email);
                if (existingUser) {
                    cogoToast.success("Profile already completed!");
                    if (existingUser.accountType === "partner") {
                        history.push("/partner");
                    } else {
                        history.push("/");
                    }
                    return;
                }
            } catch (err) {
                console.error("Error checking existing user profile:", err);
            }

            setCurrentUser(user);

            // Prefill info from Google account
            let fname = "";
            let lname = "";
            if (user.displayName) {
                const parts = user.displayName.split(" ");
                fname = parts[0] || "";
                lname = parts.slice(1).join(" ") || "";
            }

            setForm({
                firstname: fname,
                lastname: lname,
                username: user.email ? user.email.split("@")[0] : "",
                email: user.email || "",
                phone: user.phoneNumber || "",
            });

            setPartnerForm(pf => ({
                ...pf,
                businessEmail: user.email || "",
                businessPhone: user.phoneNumber || "",
            }));

            setLoading(false);
        };

        checkAuth();
    }, [auth, history]);

    const set = (field) => (e) => { const v = e.target.value; setForm(f => ({ ...f, [field]: v })); };
    const setP = (field) => (e) => { const v = e.target.value; setPartnerForm(f => ({ ...f, [field]: v })); };

    const toggleArea = (area) => {
        setPartnerForm(f => ({
            ...f,
            serviceAreas: f.serviceAreas.includes(area)
                ? f.serviceAreas.filter(a => a !== area)
                : [...f.serviceAreas, area]
        }));
    };

    const validate = () => {
        const e = {};
        if (!form.firstname.trim() || form.firstname.length < 3) e.firstname = "Min 3 characters required";
        if (!form.lastname.trim() || form.lastname.length < 3) e.lastname = "Min 3 characters required";
        if (!form.username.trim() || form.username.length < 3) e.username = "Min 3 characters required";
        if (!form.phone || form.phone.length < 10) e.phone = "Min 10 digits required";
        if (accountType === "partner") {
            if (!partnerForm.businessName.trim()) e.businessName = "Business name required";
            if (!partnerForm.businessAddress.trim()) e.businessAddress = "Business address required";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        setServerError("");
        try {
            const uid = currentUser.uid;

            // Save base user doc
            await addUser({
                uid,
                FirstName: form.firstname,
                LastName: form.lastname,
                Username: form.username,
                Email: form.email,
                Phone: form.phone,
                accountType,
                partnerType: accountType === "partner" ? partnerType : "",
                role: "user",
                createdAt: new Date().toISOString(),
            });

            // Save partner application doc
            if (accountType === "partner") {
                await addPartner({
                    uid,
                    name: `${form.firstname} ${form.lastname}`,
                    email: form.email,
                    phone: form.phone,
                    partnerType,
                    businessName: partnerForm.businessName,
                    businessAddress: partnerForm.businessAddress,
                    businessPhone: partnerForm.businessPhone || form.phone,
                    businessEmail: partnerForm.businessEmail || form.email,
                    serviceAreas: partnerForm.serviceAreas,
                    description: partnerForm.description,
                    vehicleType: partnerForm.vehicleType,
                    licenseNumber: partnerForm.licenseNumber,
                    warehouseCapacity: partnerForm.warehouseCapacity,
                    certifications: partnerForm.certifications,
                    verificationStatus: "pending",
                    submittedAt: new Date().toISOString(),
                });
                cogoToast.success("Partner application submitted! We'll review and approve your account shortly.");
                history.push('/partner');
            } else {
                cogoToast.success("Profile completed successfully!");
                history.push('/');
            }
        } catch (err) {
            setServerError(err.message.replace("Firebase: ", ""));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <AnimationRevealPage>
                <Navbar />
                <Container style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <div style={{ textAlign: "center", color: "white" }}>
                        <span className="spinner-border spinner-border-lg" style={{ display: "block", margin: "0 auto 1rem auto" }}></span>
                        <p>Loading session details...</p>
                    </div>
                </Container>
            </AnimationRevealPage>
        );
    }

    const pt = PARTNER_TYPES.find(p => p.key === partnerType);

    return (
        <AnimationRevealPage>
            <Navbar />
            <Container>
                <Content>
                    <MainContainer>
                        <LogoImage src={logo} />
                        <MainContent>
                            <Heading>Complete Your Profile</Heading>
                            <Subheading>Just a few details to finish setting up your account on {APP_NAME}.</Subheading>

                            {/* Account type selector */}
                            <TypeTabRow>
                                <TypeTab type="button" active={accountType === "customer"} onClick={() => setAccountType("customer")}>
                                    🛒 Customer
                                </TypeTab>
                                <TypeTab type="button" active={accountType === "partner"} onClick={() => setAccountType("partner")}>
                                    🤝 Partner / Business
                                </TypeTab>
                            </TypeTabRow>

                            {/* Partner sub-type selector */}
                            {accountType === "partner" && (
                                <>
                                    <SubTabRow>
                                        {PARTNER_TYPES.map(pt => (
                                            <SubTab key={pt.key} type="button" active={partnerType === pt.key} onClick={() => setPartnerType(pt.key)}>
                                                {pt.label}
                                            </SubTab>
                                        ))}
                                    </SubTabRow>
                                    {pt && <InfoBox>{pt.desc}</InfoBox>}
                                </>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <SectionLabel>Personal Information</SectionLabel>
                                
                                <Input placeholder="First name" value={form.firstname} onChange={set("firstname")} />
                                {errors.firstname && <ErrorMsg>{errors.firstname}</ErrorMsg>}
                                
                                <Input placeholder="Last name" value={form.lastname} onChange={set("lastname")} />
                                {errors.lastname && <ErrorMsg>{errors.lastname}</ErrorMsg>}
                                
                                <Input placeholder="Username" value={form.username} onChange={set("username")} />
                                {errors.username && <ErrorMsg>{errors.username}</ErrorMsg>}
                                
                                <Input type="tel" placeholder="Phone Number" value={form.phone} onChange={set("phone")} />
                                {errors.phone && <ErrorMsg>{errors.phone}</ErrorMsg>}
                                
                                <Input type="email" placeholder="Email" value={form.email} disabled style={{ opacity: 0.7, cursor: "not-allowed" }} />

                                {/* Partner-specific fields */}
                                {accountType === "partner" && (
                                    <>
                                        <SectionLabel>Business Information</SectionLabel>
                                        <Input placeholder="Business / Market Name *" value={partnerForm.businessName} onChange={setP("businessName")} />
                                        {errors.businessName && <ErrorMsg>{errors.businessName}</ErrorMsg>}
                                        
                                        <Input placeholder="Business Address *" value={partnerForm.businessAddress} onChange={setP("businessAddress")} />
                                        {errors.businessAddress && <ErrorMsg>{errors.businessAddress}</ErrorMsg>}
                                        
                                        <Input placeholder="Business Phone (if different)" value={partnerForm.businessPhone} onChange={setP("businessPhone")} />
                                        <Input type="email" placeholder="Business Email (if different)" value={partnerForm.businessEmail} onChange={setP("businessEmail")} />
                                        <Textarea placeholder="Brief description of your business / services" value={partnerForm.description} onChange={setP("description")} />

                                        {/* Dispatcher-specific */}
                                        {(partnerType === "dispatcher") && (
                                            <>
                                                <SectionLabel>Delivery Details</SectionLabel>
                                                <Input placeholder="Vehicle Type (e.g. Motorcycle, Van, Truck)" value={partnerForm.vehicleType} onChange={setP("vehicleType")} />
                                                <Input placeholder="Driver's License Number" value={partnerForm.licenseNumber} onChange={setP("licenseNumber")} />
                                            </>
                                        )}

                                        {/* Logistics-specific */}
                                        {partnerType === "logistics" && (
                                            <>
                                                <SectionLabel>Logistics Details</SectionLabel>
                                                <Input placeholder="Warehouse Capacity (e.g. 500 sqm)" value={partnerForm.warehouseCapacity} onChange={setP("warehouseCapacity")} />
                                                <Input placeholder="Certifications (comma-separated)" value={partnerForm.certifications} onChange={setP("certifications")} />
                                            </>
                                        )}

                                        {/* Farm/Wholesale certifications */}
                                        {(partnerType === "farm" || partnerType === "wholesale") && (
                                            <>
                                                <SectionLabel>Certifications / Registrations</SectionLabel>
                                                <Input placeholder="e.g. NAFDAC, CAC, Organic Cert (comma-separated)" value={partnerForm.certifications} onChange={setP("certifications")} />
                                            </>
                                        )}

                                        <SectionLabel>Service Areas</SectionLabel>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.25rem" }}>
                                            {SERVICE_AREAS.map(area => (
                                                <button
                                                    key={area} type="button"
                                                    onClick={() => toggleArea(area)}
                                                    style={{
                                                        padding: "4px 12px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer",
                                                        background: partnerForm.serviceAreas.includes(area) ? "#10b981" : "#f3f4f6",
                                                        color: partnerForm.serviceAreas.includes(area) ? "white" : "#374151",
                                                        border: partnerForm.serviceAreas.includes(area) ? "1px solid #10b981" : "1px solid #d1d5db"
                                                    }}
                                                >
                                                    {area}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {serverError && <ErrorMsg style={{ fontSize: "0.85rem", marginTop: "0.75rem" }}>{serverError}</ErrorMsg>}

                                <SubmitButton type="submit" disabled={submitting}>
                                    {submitting && <span className="spinner-border spinner-border-sm" />}
                                    <SignUpIcon className="icon" />
                                    <span className="text">
                                        {accountType === "partner" ? "Submit Partner Application" : "Complete Registration"}
                                    </span>
                                </SubmitButton>
                            </Form>
                            </MainContent>
                            {/* Render Change Password */}
                            <div tw="mt-12 w-full border-t border-gray-200 pt-8">
                                <ChangePassword />
                            </div>
                        </MainContainer>
                        <IllustrationContainer>
                            <IllustrationImage imageSrc={illustration} />
                        </IllustrationContainer>
                    </Content>
                </Container>
            </AnimationRevealPage>
    );
}
